import React, { useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import ConfirmDeleteDialog from '../components/dashboard/ConfirmDeleteDialog';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import DashboardToast from '../components/dashboard/DashboardToast';
import JobActionsMenu from '../components/dashboard/JobActionsMenu';
import JobDetailsDrawer from '../components/dashboard/JobDetailsDrawer';
import JobsPanel from '../components/dashboard/JobsPanel';
import { useDashboardJobs } from '../components/dashboard/hooks/useDashboardJobs';
import { useJobActions } from '../components/dashboard/hooks/useJobActions';
import { useJobSelection } from '../components/dashboard/hooks/useJobSelection';
import { useToast } from '../components/dashboard/hooks/useToast';
import { DashboardJob, JobKind, StatusFilter, countJobs, filterJobs, hasFiles } from '../components/dashboard/jobModel';

const NO_KINDS: ReadonlySet<JobKind> = new Set();

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

const DashboardPage: React.FC = () => {
  const { jobs, loaded, refreshing, refresh, refetch } = useDashboardJobs();
  const { busy, download, remove } = useJobActions(refetch);
  const { toast, notify, dismiss } = useToast();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [kindFilter, setKindFilter] = useState<ReadonlySet<JobKind>>(NO_KINDS);
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ jobId: string; anchorEl: HTMLElement } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DashboardJob[] | null>(null);
  const [deleting, setDeleting] = useState(false);

  const counts = useMemo(() => countJobs(jobs), [jobs]);
  const visibleJobs = useMemo(() => filterJobs(jobs, statusFilter, kindFilter), [jobs, statusFilter, kindFilter]);
  const jobsById = useMemo(() => new Map(jobs.map((job) => [job.id, job])), [jobs]);
  const selection = useJobSelection(visibleJobs);
  const { clear: clearSelection } = selection;
  const downloadableSelected = useMemo(() => selection.selectedJobs.filter(hasFiles), [selection.selectedJobs]);

  const changeStatusFilter = useCallback(
    (value: StatusFilter) => {
      setStatusFilter(value);
      clearSelection();
    },
    [clearSelection],
  );

  const toggleKind = useCallback(
    (kind: JobKind) => {
      setKindFilter((prev) => {
        const next = new Set(prev);
        if (!next.delete(kind)) next.add(kind);
        return next;
      });
      clearSelection();
    },
    [clearSelection],
  );

  const resetFilters = useCallback(() => {
    setStatusFilter('all');
    setKindFilter(NO_KINDS);
  }, []);

  const copyJobId = useCallback(
    (id: string) => {
      navigator.clipboard
        .writeText(id)
        .then(() => notify('Job ID copied'))
        .catch(() => notify('Could not copy job ID', 'error'));
    },
    [notify],
  );

  const downloadJobs = useCallback(
    async (targets: DashboardJob[]) => {
      const failures = await download(targets);
      if (failures) notify(`Failed to download ${plural(failures, 'job')}`, 'error');
    },
    [download, notify],
  );

  const downloadJob = useCallback((job: DashboardJob) => downloadJobs([job]), [downloadJobs]);
  const downloadSelected = useCallback(() => downloadJobs(downloadableSelected), [downloadJobs, downloadableSelected]);

  const openMenu = useCallback((jobId: string, anchorEl: HTMLElement) => setMenu({ jobId, anchorEl }), []);
  const closeMenu = useCallback(() => setMenu(null), []);
  const closeDrawer = useCallback(() => setOpenJobId(null), []);

  const requestDelete = useCallback(
    (id: string) => {
      const job = jobsById.get(id);
      if (job) setPendingDelete([job]);
    },
    [jobsById],
  );
  const requestDeleteSelected = useCallback(() => setPendingDelete(selection.selectedJobs), [selection.selectedJobs]);
  const cancelDelete = useCallback(() => setPendingDelete(null), []);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const failures = await remove(pendingDelete);
    setDeleting(false);
    setPendingDelete(null);
    clearSelection();
    if (failures) notify('Some jobs could not be deleted', 'error');
    else notify(pendingDelete.length > 1 ? `${pendingDelete.length} jobs deleted` : 'Job deleted');
  }, [pendingDelete, remove, clearSelection, notify]);

  const openJob = openJobId ? jobsById.get(openJobId) : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1480, width: '100%' }}>
      <DashboardHeader refreshing={refreshing} onRefresh={refresh} />

      <JobsPanel
        loading={!loaded}
        totalJobs={jobs.length}
        visibleJobs={visibleJobs}
        counts={counts}
        statusFilter={statusFilter}
        kindFilter={kindFilter}
        selectedIds={selection.selectedIds}
        selectAllState={selection.selectAllState}
        downloadableSelectedCount={downloadableSelected.length}
        busy={busy}
        bulkBusy={deleting}
        onStatusFilterChange={changeStatusFilter}
        onToggleKind={toggleKind}
        onResetFilters={resetFilters}
        onToggleAll={selection.toggleAll}
        onClearSelection={clearSelection}
        onDownloadSelected={downloadSelected}
        onDeleteSelected={requestDeleteSelected}
        onToggle={selection.toggle}
        onOpen={setOpenJobId}
        onOpenMenu={openMenu}
        onDownload={downloadJob}
        onCopyId={copyJobId}
      />

      <JobActionsMenu
        anchorEl={menu?.anchorEl ?? null}
        jobId={menu?.jobId ?? null}
        onClose={closeMenu}
        onView={setOpenJobId}
        onCopyId={copyJobId}
        onDelete={requestDelete}
      />
      <JobDetailsDrawer
        job={openJob}
        downloading={openJob ? busy[openJob.id] === 'download' : false}
        onClose={closeDrawer}
        onDownload={downloadJob}
        onDelete={requestDelete}
      />
      <ConfirmDeleteDialog jobs={pendingDelete} deleting={deleting} onCancel={cancelDelete} onConfirm={confirmDelete} />
      <DashboardToast toast={toast} onClose={dismiss} />
    </Box>
  );
};

export default DashboardPage;
